import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { OrganizationLevelType, OrganizationReferenceType, OrganizationType } from '@/api/organizations'
import { organizationHierarchyQueryOptions } from '@/api/organizationsQueryOptions'
import { ApiErrorComponent } from '@/components/ApiErrorComponent'
import useGridPageSize from '@/components/hooks/useGridPageSize'
import { usePersistedFilterModel } from '@/components/hooks/usePersistedFilterModel'
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar'
import { Box } from '@mui/material'
import {
    GridCellParams,
    GridColDef,
    GridColumnVisibilityModel,
    GridFilterModel
} from '@mui/x-data-grid'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import { useFlag } from '@unleash/proxy-client-react'
import { t } from 'i18next'
import React from 'react'

export const Route = createFileRoute('/_auth/organizationHierarchy')({
    component: OrganizationHierarchyComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

type EnhancedOrganizationType = OrganizationType & { kolonne?: OrganizationReferenceType };

function OrganizationHierarchyComponent() {
    const organizationHierarchyQuery = useSuspenseQuery(
        organizationHierarchyQueryOptions(),
    )
    const organizationHierarchyRaw = organizationHierarchyQuery.data
    const scoregCoreFeatures = useFlag('scoreg_core_features');

    const scoregDemoOrganizations = ['SCO', 'SC1', 'SC2']

    const organizationHierarchy = scoregCoreFeatures ? organizationHierarchyRaw : organizationHierarchyRaw
        .filter((org) => { return !scoregDemoOrganizations.includes(org.abbreviation || '') })
        .filter((org) => { return !org.dissolvedYear })

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []

    if ('true' === t('visibility.organizationOverview.abbreviation')) {
        columns.push(
            {
                field: 'abbreviation',
                headerName: 'Abkürzung',
                minWidth: 100,
                maxWidth: 150,
            })
    }

    columns.push(
        {
            field: 'color',
            headerName: 'Farbe',
            width: 25,
            disableExport: true,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                        <Box
                            sx={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: params.row.color,
                                borderRadius: '50%',
                            }}
                        />
                    </Box>
                )
            },
        },
        {
            field: 'name',
            headerName: 'Organisation',
            minWidth: 150,
        },
        {
            field: 'parentOrganization',
            headerName: t('enum.organizationLevel.DIVISION'),
            valueGetter: (_value: string, row: any) => {
                if (row.organizationLevel === OrganizationLevelType.TROOP) {
                    return row.parent?.name || ''
                }
                if (row.organizationLevel === OrganizationLevelType.DIVISION) {
                    return row.name
                }
                return ''
            },
            minWidth: 150,
        },
        {
            field: 'street',
            headerName: t('property.member.street'),
            minWidth: 150,
        },
        {
            field: 'postcode',
            headerName: t('property.member.postcode'),
            minWidth: 150,
        },
        {
            field: 'city',
            headerName: t('property.member.city'),
            minWidth: 150,
        },
        {
            field: 'emailPrimary',
            headerName: t('property.member.emailPrimary'),
            minWidth: 150,
        },
        {
            field: 'website',
            headerName: t('property.organization.website'),
            minWidth: 150,
        },
        {
            field: 'active',
            headerName: t('property.general.active'),
            minWidth: 150,
            type: 'boolean',
        },
        {
            field: 'dissolvedYear',
            headerName: t('property.organization.dissolvedYear'),
            minWidth: 150,
            type: 'number',
        },
    )

    const groupingColDef = {
        headerName: 'Name',
        minWidth: 100,
        valueGetter: (_value: string, row: any) => {
            return row.name
        },
    }

    function getTreeDataPath(row: OrganizationType) {
        const org = row as EnhancedOrganizationType;
        if (row.organizationLevel === OrganizationLevelType.FEDERATION) {
            return [row.uuid]
        }
        if (row.organizationLevel === OrganizationLevelType.DIVISION) {
            return [row.parent?.uuid || '', row.uuid]
        }
        if (row.organizationLevel === OrganizationLevelType.TROOP) {
            // find federation and division                        
            const federation = organizationHierarchy.find((org) => org.organizationLevel === OrganizationLevelType.FEDERATION)
            const division = organizationHierarchy.find((org) => org.uuid === row.parent?.uuid)
            // check if troop is assigned to a kolonne
            if (org.kolonne) {
                return [division?.parent?.uuid || '', row.parent?.uuid || '', org?.kolonne?.uuid || '', row.uuid]
            }
            if (!division) {
                return [federation?.uuid || '', row.uuid]
            }
            return [division?.parent?.uuid || '', row.parent?.uuid || '', row.uuid]
        }
        if (row.organizationLevel === OrganizationLevelType.KOLONNE) {
            // find federation and division
            const division = organizationHierarchy.find((org) => org.uuid === row.parent?.uuid)
            return [division?.parent?.uuid || '', row.parent?.uuid || '', row.uuid]
        }
        return [row.uuid]
    }

    const initialVisibilityModel = {
        member: xsmall || small || medium,
        firstname: large || xlarge,
        lastname: large || xlarge,
        scoutId: large || xlarge,
        active: false,
        dissolvedYear: false,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('organizations', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('organizations', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('organizations', 35));

    const initialFilterModel = {
        items: [
            { field: 'dissolvedYear', operator: 'isEmpty', value: '' },
        ],
    } as GridFilterModel
    const [filterModel, setFilterModel] = usePersistedFilterModel('organizationHierarchy', initialFilterModel)
    const handleFilterModelChange = React.useCallback((model: GridFilterModel) => {
        setFilterModel(model);
    }, [setFilterModel]);

    if (
        organizationHierarchyQuery.isLoading ||
        organizationHierarchyQuery.isFetching ||
        !organizationHierarchy
    ) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 5px)` }
        }}>
            <Box
                sx={{
                    width: { xs: '100%' },
                    height: { xs: '100%' }
                }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <DataGridPremium
                        treeData
                        getTreeDataPath={(row) => getTreeDataPath(row)}
                        groupingColDef={groupingColDef}
                        columns={columns}
                        rows={organizationHierarchy}
                        getRowId={(row) => {
                            return row.uuid || ''
                        }}
                        getCellClassName={(params: GridCellParams<any, any, number>) => {
                            return params.row.active && !params.row.dissolvedYear ? 'scoreg-active' : 'scoreg-inactive'
                        }}
                        defaultGroupingExpansionDepth={1}
                        pagination
                        pageSizeOptions={pageSizeOptions}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(model) => {
                            setPaginationModel(model)
                            gridPageSize.handlePaginationModelChange('organizations', model)
                        }}
                        columnVisibilityModel={visibilityModel}
                        onColumnVisibilityModelChange={handleVisibilityModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        slots={{ toolbar: (props) => { return <OverviewGridToolbar {...props} scope="organizations" /> } }}
                        slotProps={{
                            toolbar: {
                                csvOptions: {
                                    disableToolbarButton: true
                                },
                                printOptions: {
                                    disableToolbarButton: true
                                }
                            }
                        }}
                        density='compact'
                        hideFooter={true}
                        disableDensitySelector
                        disableAggregation
                        disableColumnPinning
                        disableColumnReorder
                        disableRowGrouping
                        disableRowSelectionOnClick
                        disablePivoting
                        showToolbar />
                </div>
            </Box>
        </Box>
    );
}
